import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { ChartData, ChartOptions } from 'chart.js';
import { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { BasicChartProps } from '../DashboardWidget';
import ChartLegend from './ChartLegend/ChartLegend';
import { defaultChartAnimation, chartMaxHeight } from './chartConfig';
import { MatrixedChartData, buildChartColors, legendMouseLeave, legendMouseOver } from './chartUtils';
import NoDataOverlay from './NoDataOverlay';

interface BarChartProps extends BasicChartProps {
  data: BarChartData;
}

export interface BarChartData extends MatrixedChartData {
  noDataMessage: null | string;
}

const BarChart = ({ title, data, colors: colorMap, loading, animation = defaultChartAnimation }: BarChartProps) => {
  const barRef = useRef();
  const theme = useTheme();
  const chartColors = buildChartColors(data.values.length, data.colors, colorMap);

  function buildDataSets(data: BarChartData) {
    const datasets: ChartData<'bar', unknown, unknown>['datasets'] = [];

    for (let i = 0; i < data.values.length; i++) {
      // starts over color array if there are more datasets than colors
      const colorIndex = i % chartColors.length;

      datasets.push({
        label: data.values[i].label,
        data: data.values[i].data,
        borderColor: chartColors[colorIndex].end,
        backgroundColor: chartColors[colorIndex].end
      });
    }

    return datasets;
  }

  // ChartJS Options
  const chartOptions: ChartOptions<'bar'> = {
    maintainAspectRatio: false,
    responsive: true,
    animation: animation,
    plugins: {
      title: {
        font: {
          family: theme.typography.h1.fontFamily
        },
        display: true,
        text: data.axes.y,
        position: 'left',
        color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        enabled: true
      },
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: theme.palette.grey[500],
          font: {
            family: theme.typography.h1.fontFamily
          }
        }
      },
      y: {
        grid: {
          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
        },
        ticks: {
          font: {
            family: theme.typography.h1.fontFamily
          },
          color: theme.palette.grey[500],
          precision: 0,
          callback: (value: any) => {
            if (typeof value === 'number') return value;
          }
          // stepSize: calculatedMaxAndStepSize.stepSize
        }
        // max: calculatedMaxAndStepSize.max
      }
    }
  };

  // ChartJS Data
  const chartData: ChartData<'bar', unknown, unknown> = {
    labels: data.labels,
    datasets: buildDataSets(data)
  };

  return (
    <Wrapper>
      {data.noDataMessage && !loading && <NoDataOverlay message={data.noDataMessage} />}

      <Chart>
        <Bar ref={barRef} data={chartData} options={chartOptions} height={chartMaxHeight} />
      </Chart>
      <Details>
        <ChartLegend
          data={data}
          colors={chartColors}
          loading={loading}
          marker="square"
          skeletonOrientation="horizontal"
          mouseOverCallback={(i: any) => legendMouseOver(barRef, i, true)}
          mouseLeaveCallback={() => legendMouseLeave(barRef)}
        />
      </Details>
    </Wrapper>
  );
};

export default BarChart;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
  margin: auto;
  position: relative;
  width: 100%;
`;

const Chart = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  width: 100%;
  margin: auto;

  > canvas {
    max-width: 100%;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 2, 0, 2)};
`;
